import { c as _c } from "react/compiler-runtime";
import { IcoLayer0116, IcoPlusSmall16 } from '@onefootprint/icons';
import { Form, IconButton, type TemplateInputProps, TextInput } from '@onefootprint/ui';
import { Controller, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CtaSection } from '@/components/playbook-builder/components/shared/cta-section';
import SettingsSection from '@/components/playbook-builder/components/shared/settings-section';
import type { ChoiceButtonFormData, CustomQuestionsFormData } from '../../types';
import { type ChoiceErrorKey, getLabelError, getOutputError, getValueError } from '../../utils/choice-errors';

// Typed to the one field this section edits: RHF's paths over the whole form's `Question` union
// exceed TS's instantiation depth (TS2589).
type ChoiceFields = Pick<CustomQuestionsFormData, 'choice'>;
type ButtonsSectionControls = {
  selectedId: string | null;
  openIds: Record<string, boolean>;
  onButtonOpenChange: (id: string, open: boolean) => void;
  onAdd: () => void;
  onDelete: (id: string, index: number) => void;
  /** With no buttons yet, opening the stock Continue turns it into the page's first button. */
  onOpenStock: () => void;
  onSaveValueChange: (saveValue: boolean) => void;
};
type ButtonsSectionProps = ButtonsSectionControls & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  autocompleteItems?: TemplateInputProps['autocompleteItems'];
  canEdit: boolean;
};
const ButtonsSection = t0 => {
  const $ = _c(77);
  const {
    open,
    onOpenChange,
    selectedId,
    openIds,
    onButtonOpenChange,
    onAdd,
    onDelete,
    onOpenStock,
    onSaveValueChange,
    autocompleteItems,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-custom-questions");
  const {
    control,
    getValues,
    setValue
  } = useFormContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["choice.saveValue", "choice.buttons"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== control) {
    t2 = {
      control,
      name: t1
    };
    $[1] = control;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [saveValue, buttons] = useWatch(t2);
  let t3;
  if ($[3] !== control) {
    t3 = {
      control,
      name: "choice"
    };
    $[3] = control;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    errors
  } = useFormState(t3);
  let t4;
  if ($[5] !== getValues || $[6] !== setValue) {
    t4 = buttonIndex => next => {
      if (next !== "primary") {
        return;
      }
      getValues("choice.buttons").forEach((button, index) => {
        if (index !== buttonIndex && button.style === "primary") {
          setValue(`choice.buttons.${index}.style`, "secondary", {
            shouldDirty: true
          });
        }
      });
    };
    $[5] = getValues;
    $[6] = setValue;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const demoteOtherPrimaries = t4;
  let t5;
  if ($[8] !== t) {
    t5 = isPrimary => isPrimary ? {
      disabled: true,
      description: t(_temp)
    } : {};
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const primaryLock = t5;
  let t6;
  if ($[10] !== t) {
    t6 = key => key ? t($_0 => $_0.buttons.errors[key]) : true;
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const errorMessage = t6;
  let t7;
  if ($[12] !== saveValue || $[13] !== t) {
    t7 = (button_0, isOpen) => {
      const style = t($_1 => $_1.buttons.badges[button_0.style]);
      const value = button_0.value.trim();
      return !isOpen && saveValue && value ? t(_temp2, {
        style,
        value
      }) : style;
    };
    $[12] = saveValue;
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const badge = t7;
  let t8;
  if ($[15] !== t) {
    t8 = t(_temp3);
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== canEdit || $[18] !== onAdd || $[19] !== t) {
    t9 = canEdit ? <IconButton icon={IcoPlusSmall16} aria-label={t(_temp4)} size="tiny" variant="ghost" rounded={true} onClick={onAdd} /> : undefined;
    $[17] = canEdit;
    $[18] = onAdd;
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t) {
    t10 = t(_temp5);
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== onSaveValueChange || $[24] !== t10) {
    t11 = <Form.Toggle name="choice.saveValue" label={t10} size="compact" onChange={onSaveValueChange} />;
    $[23] = onSaveValueChange;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== control || $[27] !== errorMessage || $[28] !== getValues || $[29] !== saveValue || $[30] !== t) {
    t12 = saveValue ? <Controller control={control} name="choice.dataIdentifier" rules={{
      validate: () => errorMessage(getOutputError(getValues("choice")))
    }} render={t13 => {
      const {
        field,
        fieldState
      } = t13;
      return <Form.Group label={t(_temp6)} hint={t(_temp7)} error={fieldState.error?.message} size="compact" addon="custom." disabled={field.disabled} fullWidth={true}><TextInput aria-label={t(_temp8)} value={field.value.replace(/^custom\./, "")} disabled={field.disabled} hasError={!!fieldState.error} onChangeText={next_0 => field.onChange(next_0 ? `custom.${next_0}` : "")} onBlur={field.onBlur} size="compact" containerClassName="flex-1" className="rounded-tl-none! rounded-bl-none!" /></Form.Group>;
    }} /> : <p className="text-body-3 text-tertiary">{t(_temp9)}</p>;
    $[26] = control;
    $[27] = errorMessage;
    $[28] = getValues;
    $[29] = saveValue;
    $[30] = t;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== buttons.length || $[33] !== canEdit || $[34] !== onOpenStock || $[35] !== t) {
    t13 = buttons.length === 0 && <CtaSection title={t(_temp10)} badge={t(_temp11)} size="compact" open={false} onOpenChange={next_1 => {
      if (next_1 && canEdit) {
        onOpenStock();
      }
    }} hasError={false} removeLabel={t(_temp12)} />;
    $[32] = buttons.length;
    $[33] = canEdit;
    $[34] = onOpenStock;
    $[35] = t;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== autocompleteItems || $[38] !== badge || $[39] !== buttons || $[40] !== canEdit || $[41] !== demoteOtherPrimaries || $[42] !== errorMessage || $[43] !== errors || $[44] !== getValues || $[45] !== onButtonOpenChange || $[46] !== onDelete || $[47] !== openIds || $[48] !== primaryLock || $[49] !== saveValue || $[50] !== selectedId || $[51] !== t) {
    let t15;
    if ($[53] !== autocompleteItems || $[54] !== badge || $[55] !== canEdit || $[56] !== demoteOtherPrimaries || $[57] !== errorMessage || $[58] !== errors || $[59] !== getValues || $[60] !== onButtonOpenChange || $[61] !== onDelete || $[62] !== openIds || $[63] !== primaryLock || $[64] !== saveValue || $[65] !== selectedId || $[66] !== t) {
      t15 = (button_1, index_0) => {
        const isOpen_0 = !!openIds[button_1.id];
        const isPrimary_0 = button_1.style === "primary";
        return <CtaSection key={`${button_1.id}-${index_0}`} title={button_1.label.trim() || t(_temp13, {
          index: index_0 + 1
        })} badge={badge(button_1, isOpen_0)} size="compact" open={isOpen_0} onOpenChange={next_2 => onButtonOpenChange(button_1.id, next_2)} isSelected={selectedId === button_1.id} hasError={!!errors.choice?.buttons?.[index_0]} removeLabel={t(_temp14)} onRemove={canEdit && !isPrimary_0 ? () => onDelete(button_1.id, index_0) : undefined}><Form.TemplateInput name={`choice.buttons.${index_0}.label`} label={t(_temp15)} placeholder={t(_temp16)} size="compact" autocompleteItems={autocompleteItems ?? {}} rules={{
            validate: () => errorMessage(getLabelError(getValues("choice"), index_0))
          }} /><Form.Select name={`choice.buttons.${index_0}.style`} label={t(_temp17)} size="compact" onChange={demoteOtherPrimaries(index_0)} options={[{
            value: "primary",
            label: t(_temp18)
          }, {
            value: "secondary",
            label: t(_temp19),
            ...primaryLock(isPrimary_0)
          }, {
            value: "link",
            label: t(_temp20),
            ...primaryLock(isPrimary_0)
          }]} />{saveValue && <Form.Input name={`choice.buttons.${index_0}.value`} label={t(_temp21)} hint={t(_temp22)} placeholder={t(_temp23)} size="compact" rules={{
            validate: () => errorMessage(getValueError(getValues("choice"), index_0))
          }} />}</CtaSection>;
      };
      $[53] = autocompleteItems;
      $[54] = badge;
      $[55] = canEdit;
      $[56] = demoteOtherPrimaries;
      $[57] = errorMessage;
      $[58] = errors;
      $[59] = getValues;
      $[60] = onButtonOpenChange;
      $[61] = onDelete;
      $[62] = openIds;
      $[63] = primaryLock;
      $[64] = saveValue;
      $[65] = selectedId;
      $[66] = t;
      $[67] = t15;
    } else {
      t15 = $[67];
    }
    t14 = buttons.map(t15);
    $[37] = autocompleteItems;
    $[38] = badge;
    $[39] = buttons;
    $[40] = canEdit;
    $[41] = demoteOtherPrimaries;
    $[42] = errorMessage;
    $[43] = errors;
    $[44] = getValues;
    $[45] = onButtonOpenChange;
    $[46] = onDelete;
    $[47] = openIds;
    $[48] = primaryLock;
    $[49] = saveValue;
    $[50] = selectedId;
    $[51] = t;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[68] !== onOpenChange || $[69] !== open || $[70] !== t11 || $[71] !== t12 || $[72] !== t13 || $[73] !== t14 || $[74] !== t8 || $[75] !== t9) {
    t15 = <SettingsSection title={t8} icon={IcoLayer0116} open={open} onOpenChange={onOpenChange} actions={t9}>{t11}{t12}{t13}{t14}</SettingsSection>;
    $[68] = onOpenChange;
    $[69] = open;
    $[70] = t11;
    $[71] = t12;
    $[72] = t13;
    $[73] = t14;
    $[74] = t8;
    $[75] = t9;
    $[76] = t15;
  } else {
    t15 = $[76];
  }
  return t15;
};
export type { ButtonsSectionControls };
export default ButtonsSection;
function _temp($) {
  return $.buttons.primaryLocked;
}
function _temp2($_2) {
  return $_2.buttons.badgeWithValue;
}
function _temp3($_3) {
  return $_3.buttons.title;
}
function _temp4($_4) {
  return $_4.buttons.add;
}
function _temp5($_5) {
  return $_5.buttons.saveValue;
}
function _temp6($_6) {
  return $_6.buttons.output;
}
function _temp7($_7) {
  return $_7.buttons.outputHint;
}
function _temp8($_8) {
  return $_8.buttons.output;
}
function _temp9($_9) {
  return $_9.buttons.saveValueHint;
}
function _temp10($_10) {
  return $_10.preview.cta;
}
function _temp11($_11) {
  return $_11.buttons.badges.primary;
}
function _temp12($_12) {
  return $_12.preview.deleteButton;
}
function _temp13($_13) {
  return $_13.buttons.button;
}
function _temp14($_14) {
  return $_14.preview.deleteButton;
}
function _temp15($_15) {
  return $_15.buttons.label;
}
function _temp16($_16) {
  return $_16.buttons.labelPlaceholder;
}
function _temp17($_17) {
  return $_17.buttons.style;
}
function _temp18($_18) {
  return $_18.buttons.styles.primary;
}
function _temp19($_19) {
  return $_19.buttons.styles.secondary;
}
function _temp20($_20) {
  return $_20.buttons.styles.link;
}
function _temp21($_21) {
  return $_21.buttons.value;
}
function _temp22($_22) {
  return $_22.buttons.valueHint;
}
function _temp23($_23) {
  return $_23.buttons.valuePlaceholder;
}